import type { IntlShape } from 'react-intl';

import { TargetEntityDetails } from '../types/entity';
import { ModelActionType } from '../types/model';
import { getEntityDisplayType, getEntityDisplayTypeLower } from './entity';

export const resolveModalProps = ({
  modalAction,
  entityDetails,
  intl,
}: {
  modalAction: 'delete' | 'suspend';
  entityDetails: TargetEntityDetails;
  intl: IntlShape;
}) => {
  switch (modalAction) {
    case 'delete':
      return {
        description: intl.formatMessage(
          {
            id: 'XRayModals.deleteConfirmation',
            defaultMessage:
              'Please confirm that you would like to delete {entityType} Model <strong>{entityLabel}</strong>. This will not delete all values previously found by the model, but will remove this model and stop it from running on any new documents that fit within the model scope. This action cannot be undone.',
            description:
              'Confirmation message for deleting a model. Example: entityType = "Field", entityLabel = "Contract Value"',
          },
          {
            entityType: getEntityDisplayType({
              entityType: entityDetails.type,
              intl,
            }),
            entityLabel: entityDetails.label,
            strong: (chunks) => `<strong>${chunks}</strong>`,
          },
        ),
        variant: 'danger' as const,
        toastMessage: intl.formatMessage(
          {
            id: 'XRayModals.deleteToast',
            defaultMessage: 'Deleted <strong>{entityLabel}</strong>',
            description:
              'Toast message when model is deleted. Example: entityLabel = "Contract Value"',
          },
          {
            entityLabel: entityDetails.label,
            strong: (chunks) => `<strong>${chunks}</strong>`,
          },
        ),
      };
    case 'suspend': {
      return {
        description: intl.formatMessage(
          {
            id: 'XRayModals.suspendConfirmation',
            defaultMessage:
              'Confirm that you would like to suspend {entityType} Model <strong>{entityLabel}</strong>. This will suspend the {entityTypeLower} identifications run by the {entityType} Model. You can resume the {entityType} Model back to the published state after suspending.',
            description:
              'Confirmation message for suspending a model. Example: entityType = "Field", entityTypeLower = "field", entityLabel = "Contract Value"',
          },
          {
            entityType: getEntityDisplayType({
              entityType: entityDetails.type,
              intl,
            }),
            entityTypeLower: getEntityDisplayTypeLower({
              entityType: entityDetails.type,
              intl,
            }),
            entityLabel: entityDetails.label,
            strong: (chunks) => `<strong>${chunks}</strong>`,
          },
        ),
        variant: 'primary' as const,
        toastMessage: intl.formatMessage(
          {
            id: 'XRayModals.suspendToast',
            defaultMessage: 'Suspended <strong>{entityLabel}</strong>',
            description:
              'Toast message when model is suspended. Example: entityLabel = "Contract Value"',
          },
          {
            entityLabel: entityDetails.label,
            strong: (chunks) => `<strong>${chunks}</strong>`,
          },
        ),
      };
    }
  }
};

export const getErrorMessage = ({
  intl,
  action,
  targetEntityLabel,
}: {
  intl: IntlShape;
  action: ModelActionType;
  targetEntityLabel: TargetEntityDetails['label'];
}) => {
  switch (action) {
    case 'delete':
      return intl.formatMessage(
        {
          id: 'ModelModal.deleteFailedMessage',
          defaultMessage:
            'Failed to delete <strong>{targetEntityLabel}</strong>. Please try again.',
          description:
            'Error message when model deletion fails. Example: targetEntityLabel = "Important"',
        },
        {
          targetEntityLabel,
          strong: (chunks) => `<strong>${chunks}</strong>`,
        },
      );
    case 'suspend':
      return intl.formatMessage(
        {
          id: 'ModelModal.suspendFailedMessage',
          defaultMessage:
            'Failed to suspend <strong>{targetEntityLabel}</strong>. Please try again.',
          description:
            'Error message when model suspension fails. Example: targetEntityLabel = "Important"',
        },
        {
          targetEntityLabel,
          strong: (chunks) => `<strong>${chunks}</strong>`,
        },
      );
    case 'resume':
      return intl.formatMessage(
        {
          id: 'ModelModal.resumeFailedMessage',
          defaultMessage:
            'Failed to resume <strong>{targetEntityLabel}</strong>. Please try again.',
          description:
            'Error message when model resume fails. Example: targetEntityLabel = "Important"',
        },
        {
          targetEntityLabel,
          strong: (chunks) => `<strong>${chunks}</strong>`,
        },
      );
    default:
      return '';
  }
};
